// Graphical Forecasts for Aviation (GFA) hazard layer family.
//
// ONE shared GeoJSON source with the full G-AIRMET FeatureCollection;
// EIGHT independently-toggleable layers filter the source by the
// `hazard` property. Per-hazard styling follows ForeFlight's color
// conventions where reasonable so pilots already trained on FF
// recognize the colors immediately.
//
// 19 MapLibre layer ids total across 8 hazard families:
//   IFR        — fill + line
//   MT_OBSC    — fill + line
//   ICE        — fill + line + label
//   TURB-LO    — fill + line + label
//   TURB-HI    — fill + line + label
//   LLWS       — fill + line
//   FZLVL      — line + label (contour, not a region)
//   SFC_WND    — fill + line
//
// Visibility logic is compound: a layer is on iff
//   useLayerVisibilityStore.layers.wxhazards   (master 'WX Hazards' toggle)
//   AND useLayerVisibilityStore.layers['gfa-<hazard>'] (per-hazard chip)
//
// In addition to visibility, each layer carries a TIME-SCRUBBER filter
// that's built from `wxHazardOffsetH` (±3h validity window around now+offset).
// addOverlays() seeded that filter at first mount; this module's
// applyState re-applies it on every basemap rebuild so the slider
// state survives a sectional→IFR→satellite→helo cycle.
//
// Extracted from addOverlays.ts as HI-1 batch 7 round 5 (2026-05-24).
// The single biggest-by-LOC extraction in batch 7 (~280 LOC). Pure
// mechanical move — source spec, paint expressions, layer ids, layer
// order, filter expressions, and minzoom all byte-identical to the
// pre-extraction form. applyState was NOT present in the original
// mount (the boss-caught batch-6b bug retrofit); it's added here so
// GFA layers survive basemap switches without needing the user to
// toggle off+on.

import type { Map as MlMap } from 'maplibre-gl';
import { EMPTY_FC } from '../MapView';
import { GFA_LAYER_IDS, HAZARD_BY_LAYER } from '../gfaLayerIds';
import { buildGfaTimeFilter } from '../mapFilters';
import { registerLayerFamily } from '../layerVisibilityRegistry';
import { useUiStore } from '../../stores/uiStore';
import { useLayerVisibilityStore } from '../../stores/layerVisibilityStore';
import { isOverlayLoaded, markOverlayLoaded, refreshOverlay } from '../overlayRuntime';
import { OVERLAY_BY_ID } from '../overlaySpecs';

/**
 * Mount the GFA source + all 19 hazard layers. Idempotent on the source.
 * Also seeds the time-scrubber filter from the current `wxHazardOffsetH`
 * so the layers honor the slider position from frame zero.
 */
export function addGfaLayers(map: MlMap): void {
  if (!map.getSource('gfa')) {
    map.addSource('gfa', { type: 'geojson', data: EMPTY_FC });

    // IFR / Low ceilings — cyan-blue.
    map.addLayer({
      id: 'gfa-ifr-fill',
      type: 'fill',
      source: 'gfa',
      filter: ['==', ['get', 'hazard'], 'IFR'],
      layout: { visibility: 'none' },
      paint: { 'fill-color': '#60a5fa', 'fill-opacity': 0.30 },
    });
    map.addLayer({
      id: 'gfa-ifr-line',
      type: 'line',
      source: 'gfa',
      filter: ['==', ['get', 'hazard'], 'IFR'],
      layout: { visibility: 'none' },
      paint: { 'line-color': '#2563eb', 'line-width': 2 },
    });

    // Mountain Obscuration — purple/orange (ForeFlight uses brown‑ish;
    // we lean purple to keep it distinct from icing's cyan).
    map.addLayer({
      id: 'gfa-mtnobscn-fill',
      type: 'fill',
      source: 'gfa',
      filter: ['==', ['get', 'hazard'], 'MT_OBSC'],
      layout: { visibility: 'none' },
      paint: { 'fill-color': '#a855f7', 'fill-opacity': 0.25 },
    });
    map.addLayer({
      id: 'gfa-mtnobscn-line',
      type: 'line',
      source: 'gfa',
      filter: ['==', ['get', 'hazard'], 'MT_OBSC'],
      layout: { visibility: 'none' },
      paint: { 'line-color': '#7e22ce', 'line-width': 2 },
    });

    // Icing — cyan, with severity-driven opacity.
    map.addLayer({
      id: 'gfa-ice-fill',
      type: 'fill',
      source: 'gfa',
      filter: ['==', ['get', 'hazard'], 'ICE'],
      layout: { visibility: 'none' },
      paint: {
        'fill-color': '#7dd3fc',
        'fill-opacity': [
          'match', ['get', 'severity'],
          'SEV', 0.45,
          'MOD', 0.30,
          /* default */ 0.25,
        ],
      },
    });
    map.addLayer({
      id: 'gfa-ice-line',
      type: 'line',
      source: 'gfa',
      filter: ['==', ['get', 'hazard'], 'ICE'],
      layout: { visibility: 'none' },
      paint: { 'line-color': '#0284c7', 'line-width': 2 },
    });
    map.addLayer({
      id: 'gfa-ice-label',
      type: 'symbol',
      source: 'gfa',
      filter: ['==', ['get', 'hazard'], 'ICE'],
      minzoom: 5,
      layout: {
        visibility: 'none',
        'text-field': [
          'concat',
          'ICE · ', ['coalesce', ['get', 'severity'], ''],
          ' · ',
          ['coalesce', ['get', 'base'], 'SFC'], '–', ['coalesce', ['get', 'top'], '—'],
        ],
        'text-size': 10,
        'text-anchor': 'center',
        'text-font': ['Noto Sans Regular'],
      },
      paint: {
        'text-color': '#bae6fd',
        'text-halo-color': '#0b0d0f',
        'text-halo-width': 1.4,
      },
    });

    // Turbulence (Low) — orange, dashed line.
    map.addLayer({
      id: 'gfa-turblo-fill',
      type: 'fill',
      source: 'gfa',
      filter: ['==', ['get', 'hazard'], 'TURB-LO'],
      layout: { visibility: 'none' },
      paint: {
        'fill-color': '#fb923c',
        'fill-opacity': [
          'match', ['get', 'severity'],
          'SEV', 0.40,
          'MOD', 0.30,
          /* default */ 0.25,
        ],
      },
    });
    map.addLayer({
      id: 'gfa-turblo-line',
      type: 'line',
      source: 'gfa',
      filter: ['==', ['get', 'hazard'], 'TURB-LO'],
      layout: { visibility: 'none' },
      paint: {
        'line-color': '#ea580c',
        'line-width': 2,
        'line-dasharray': [3, 2],
      },
    });
    map.addLayer({
      id: 'gfa-turblo-label',
      type: 'symbol',
      source: 'gfa',
      filter: ['==', ['get', 'hazard'], 'TURB-LO'],
      minzoom: 5,
      layout: {
        visibility: 'none',
        'text-field': [
          'concat',
          'TURB · ', ['coalesce', ['get', 'severity'], ''],
          ' · ',
          ['coalesce', ['get', 'base'], 'SFC'], '–', ['coalesce', ['get', 'top'], '—'],
        ],
        'text-size': 10,
        'text-anchor': 'center',
        'text-font': ['Noto Sans Regular'],
      },
      paint: {
        'text-color': '#fed7aa',
        'text-halo-color': '#0b0d0f',
        'text-halo-width': 1.4,
      },
    });

    // Turbulence (High) — same orange, muted fill (high alt = less
    // operationally relevant for most fire/HEMS work).
    map.addLayer({
      id: 'gfa-turbhi-fill',
      type: 'fill',
      source: 'gfa',
      filter: ['==', ['get', 'hazard'], 'TURB-HI'],
      layout: { visibility: 'none' },
      paint: {
        'fill-color': '#fb923c',
        'fill-opacity': 0.18,
      },
    });
    map.addLayer({
      id: 'gfa-turbhi-line',
      type: 'line',
      source: 'gfa',
      filter: ['==', ['get', 'hazard'], 'TURB-HI'],
      layout: { visibility: 'none' },
      paint: {
        'line-color': '#ea580c',
        'line-width': 1.5,
        'line-dasharray': [3, 2],
      },
    });
    map.addLayer({
      id: 'gfa-turbhi-label',
      type: 'symbol',
      source: 'gfa',
      filter: ['==', ['get', 'hazard'], 'TURB-HI'],
      minzoom: 5,
      layout: {
        visibility: 'none',
        'text-field': [
          'concat',
          'TURB HI · ',
          ['coalesce', ['get', 'base'], '—'], '–', ['coalesce', ['get', 'top'], '—'],
        ],
        'text-size': 10,
        'text-anchor': 'center',
        'text-font': ['Noto Sans Regular'],
      },
      paint: {
        'text-color': '#fed7aa',
        'text-halo-color': '#0b0d0f',
        'text-halo-width': 1.4,
      },
    });

    // Low-Level Wind Shear — yellow.
    map.addLayer({
      id: 'gfa-llws-fill',
      type: 'fill',
      source: 'gfa',
      filter: ['==', ['get', 'hazard'], 'LLWS'],
      layout: { visibility: 'none' },
      paint: { 'fill-color': '#facc15', 'fill-opacity': 0.40 },
    });
    map.addLayer({
      id: 'gfa-llws-line',
      type: 'line',
      source: 'gfa',
      filter: ['==', ['get', 'hazard'], 'LLWS'],
      layout: { visibility: 'none' },
      paint: { 'line-color': '#ca8a04', 'line-width': 2 },
    });

    // Freezing Level — LINE only (it's a contour, not a region).
    map.addLayer({
      id: 'gfa-fzlvl-line',
      type: 'line',
      source: 'gfa',
      filter: ['==', ['get', 'hazard'], 'FZLVL'],
      layout: { visibility: 'none' },
      paint: {
        'line-color': '#0284c7',
        'line-width': 2.5,
        'line-dasharray': [4, 3],
      },
    });
    map.addLayer({
      id: 'gfa-fzlvl-label',
      type: 'symbol',
      source: 'gfa',
      filter: ['==', ['get', 'hazard'], 'FZLVL'],
      minzoom: 5,
      layout: {
        visibility: 'none',
        'text-field': [
          'concat',
          'FZ · ', ['coalesce', ['get', 'level'], '—'],
        ],
        'text-size': 10,
        'text-anchor': 'center',
        'text-font': ['Noto Sans Regular'],
        'symbol-placement': 'line',
        // Repeat the label every ~250px along the line so a long
        // freezing-level contour shows multiple labels instead of
        // dropping the only one when it collides with another
        // symbol layer.
        'symbol-spacing': 250,
        // Don't let collision detection swallow our level-altitude
        // labels — they're the whole point of this layer.
        'text-allow-overlap': false,
        'text-ignore-placement': false,
        'text-padding': 2,
      },
      paint: {
        'text-color': '#bae6fd',
        'text-halo-color': '#0b0d0f',
        'text-halo-width': 1.4,
      },
    });

    // Surface winds ≥ 30 KT sustained — yellow.
    map.addLayer({
      id: 'gfa-sfcwnd-fill',
      type: 'fill',
      source: 'gfa',
      filter: ['==', ['get', 'hazard'], 'SFC_WND'],
      layout: { visibility: 'none' },
      paint: { 'fill-color': '#facc15', 'fill-opacity': 0.30 },
    });
    map.addLayer({
      id: 'gfa-sfcwnd-line',
      type: 'line',
      source: 'gfa',
      filter: ['==', ['get', 'hazard'], 'SFC_WND'],
      layout: { visibility: 'none' },
      paint: { 'line-color': '#ca8a04', 'line-width': 2 },
    });

    // Apply the current time-scrubber filter to every GFA layer we
    // just created. The React effect that re-applies on slider drag
    // is a no-op when layers don't exist yet (it runs at mount before
    // map.on('load') has fired); doing the initial apply here makes
    // the layers honor the slider position from frame zero.
    const initialOffsetH = useUiStore.getState().wxHazardOffsetH;
    for (const id of GFA_LAYER_IDS) {
      if (!map.getLayer(id)) continue;
      const hazard = HAZARD_BY_LAYER[id];
      if (!hazard) continue;
      map.setFilter(id, buildGfaTimeFilter(hazard, initialOffsetH) as any);
    }
  }
}

// Layer id → store key for per-hazard chip. The store keys are
// 'gfa-ifr', 'gfa-mtnobscn', etc.; the layer ids extend those with
// '-fill', '-line', '-label' suffixes.
const HAZARD_CHIP_KEY: Record<string, 'gfa-ifr' | 'gfa-mtnobscn' | 'gfa-ice' | 'gfa-turblo' | 'gfa-turbhi' | 'gfa-llws' | 'gfa-fzlvl' | 'gfa-sfcwnd'> = {
  'gfa-ifr-fill':       'gfa-ifr',
  'gfa-ifr-line':       'gfa-ifr',
  'gfa-mtnobscn-fill':  'gfa-mtnobscn',
  'gfa-mtnobscn-line':  'gfa-mtnobscn',
  'gfa-ice-fill':       'gfa-ice',
  'gfa-ice-line':       'gfa-ice',
  'gfa-ice-label':      'gfa-ice',
  'gfa-turblo-fill':    'gfa-turblo',
  'gfa-turblo-line':    'gfa-turblo',
  'gfa-turblo-label':   'gfa-turblo',
  'gfa-turbhi-fill':    'gfa-turbhi',
  'gfa-turbhi-line':    'gfa-turbhi',
  'gfa-turbhi-label':   'gfa-turbhi',
  'gfa-llws-fill':      'gfa-llws',
  'gfa-llws-line':      'gfa-llws',
  'gfa-fzlvl-line':     'gfa-fzlvl',
  'gfa-fzlvl-label':    'gfa-fzlvl',
  'gfa-sfcwnd-fill':    'gfa-sfcwnd',
  'gfa-sfcwnd-line':    'gfa-sfcwnd',
};

registerLayerFamily({
  id: 'gfa',
  label: 'GFA hazards',
  sourceId: 'gfa',
  layerIds: GFA_LAYER_IDS,
  mount: (map) => addGfaLayers(map),
  // visibilitySelector: family is "conceptually on" when the master
  // wx-hazards toggle is on AND at least one chip is on. Used for
  // OFF→ON detection (to trigger onToggleOn data fetch). The actual
  // per-layer visibility is managed by applyState below.
  visibilitySelector: (l) => l.wxhazards && (
    l['gfa-ifr'] || l['gfa-mtnobscn'] || l['gfa-ice'] ||
    l['gfa-turblo'] || l['gfa-turbhi'] || l['gfa-llws'] ||
    l['gfa-fzlvl'] || l['gfa-sfcwnd']
  ),
  onToggleOn: (map) => {
    const spec = OVERLAY_BY_ID.get('gfa');
    if (!spec || isOverlayLoaded('gfa')) return;
    markOverlayLoaded('gfa');
    return refreshOverlay(map, spec);
  },
  // applyState handles BOTH visibility (compound: wxhazards master
  // && per-hazard chip) AND the time-scrubber filter.
  applyState: (map) => {
    if (!map.getLayer('gfa-ifr-fill')) return;
    const layers = useLayerVisibilityStore.getState().layers;
    const master = layers.wxhazards;
    const offsetH = useUiStore.getState().wxHazardOffsetH;
    for (const layerId of GFA_LAYER_IDS) {
      if (!map.getLayer(layerId)) continue;
      const chipKey = HAZARD_CHIP_KEY[layerId];
      const perChip = chipKey ? layers[chipKey] : false;
      const on = master && perChip;
      map.setLayoutProperty(layerId, 'visibility', on ? 'visible' : 'none');
      // Re-apply the time-scrubber filter — the rebuild created a
      // fresh layer with only the hazard-match filter, not the
      // validity-window combined filter that the slider expects.
      const hazard = HAZARD_BY_LAYER[layerId];
      if (hazard) {
        map.setFilter(layerId, buildGfaTimeFilter(hazard, offsetH) as any);
      }
    }
  },
});
